import React from 'react';

import {
  GENDER_OPTIONS, MASK_OPTIONS, DEEPFAKE_STATE_OPTIONS, EMOTION_OPTIONS,
} from '@vlabs/api-bindings/src/constants';
import { LIVENESS_OPTIONS, EVENT_ENUMS } from '@vlabs/api-bindings/src/luna-client/constants';
import validate from '@vlabs/shared/validators';
import { Control, SettingsItemWrapper } from '@vlabs/uikit';
import { useFormContext, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { selectSourceOptionsByAccount } from '@vlabs/pages/sources/selectors';

import { useGetLists } from '@vlabs/api/luna-api/client';
import type { GeoPositionBbox, ValueFilter } from '@vlabs/api/luna-api/model';
import { ControllableValueSelect } from '@vlabs/monokit/shared/ui/value-select';

import { useChartFormTranslation } from '../i18n';
import st from './FiltersForm.module.sass';
import { GeopositionFilterSubForm } from './GeopositionFilterSubForm';

interface FilterValueControlProps {
  disabled: boolean;
  fieldConfig: Record<string, any>;
  filterIndex: number;
  value: ValueFilter;
}

export function FilterValueControl({
  disabled,
  fieldConfig,
  filterIndex,
  value,
}: FilterValueControlProps): React.JSX.Element {
  const { t } = useChartFormTranslation();
  const {
    control,
    clearErrors,
    register,
    setValue,
    formState: { errors },
  } = useFormContext();
  const camOptions = useSelector(selectSourceOptionsByAccount);

  const getLists = useGetLists({ page_size: 1000 });
  const listOptions = getLists.data?.lists?.map((list) => ({
    label: list.user_data,
    value: list.list_id,
  })) ?? [];

  const optionsForSelect = {
    lists: listOptions,
    source: camOptions,
    gender: GENDER_OPTIONS.raw,
    emotion: EMOTION_OPTIONS.raw,
    mask: MASK_OPTIONS.raw,
    liveness: LIVENESS_OPTIONS.raw,
    deepfake: DEEPFAKE_STATE_OPTIONS.raw,
    apparent_gender: EVENT_ENUMS.BODY_BASIC_ATTRIBUTES.APPARENT_GENDER.raw,
    sleeve_length: EVENT_ENUMS.UPPER_BODY.SLEEVE.raw,
    headwear_state: EVENT_ENUMS.UPPER_BODY.HEADWEAR.raw,
    upper_clothing_colors: EVENT_ENUMS.UPPER_BODY.UPPER_CLOSING_COLOR.raw,
    backpack_state: EVENT_ENUMS.ACCESSORIES.BACKPACK.raw,
    lower_garment_colors: EVENT_ENUMS.LOWER_BODY.LOWER_GARMENT.COLORS.raw,
    headwear_apparent_color: EVENT_ENUMS.UPPER_BODY.HEADWEAR_COLORS.raw,
    lower_garment_type: EVENT_ENUMS.LOWER_BODY.LOWER_GARMENT.TYPE.raw,
    shoes_apparent_color: EVENT_ENUMS.LOWER_BODY.SHOES.APPARENT_COLOR.raw,
  };

  const name = `getEventStats.filters.${filterIndex}.value`;
  const handleGeolocationChange = (newValue: GeoPositionBbox) => {
    setValue(name, newValue);
    clearErrors(name);
  };

  const renderValueControl = () => {
    if (fieldConfig?.format === 'select') {
      const { optionsKey } = fieldConfig;

      return (
        <ControllableValueSelect
          control={control}
          disabled={disabled}
          errors={errors}
          isMulti={fieldConfig.isMulti}
          name={name}
          options={optionsForSelect?.[optionsKey as keyof typeof optionsForSelect]}
          rules={{
            required: validate.required(),
            validate: null,
          }}
        />
      );
    }

    if (fieldConfig?.format === 'geolocation') {
      return (
        <React.Fragment>
          <GeopositionFilterSubForm
            disabled={disabled}
            initialValues={value as GeoPositionBbox}
            onSubmit={handleGeolocationChange}
          />
          <Control.Checkbox
            className={st.Hidden}
            errors={errors}
            id={name}
            {...register(name, {
              required: validate.required(),
              validate: null,
            })}
          />
        </React.Fragment>
      );
    }

    if (fieldConfig?.format === 'input') {
      return (
        /*
        Нужен Controller вместо {...register(...)}, т.к. выше у нас есть Select c Controller и требуется,
        чтобы регистрация полей для одного значения была одинаковой, иначе происходит конфликт регистраций
        и валидация инпутов при смене типа поля присваивается селектам
        */
        <Controller
          control={control}
          name={name}
          render={({ field, fieldState: { error } }) => (
            <Control.Input
              {...field}
              disabled={disabled}
              errors={{ [name]: error }}
              id={name}
            />
          )}
          rules={{
            required: validate.required(),
            validate: fieldConfig?.validateRule?.(),
          }}
        />
      );
    }

    return (
      <Control.Input
        disabled
      />
    );
  };

  return (
    <SettingsItemWrapper
      contentClassName={st.SettingsItem__Content}
      data-tooltip-content={(disabled && t('form.filters.value.tooltip')) as unknown as string}
      disabled={disabled}
      title={t('form.filters.value.label')}
      titleClassName={st.SettingsItem__Title}
    >
      {renderValueControl()}
    </SettingsItemWrapper>
  );
}
